/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Avatar, Dropdown } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { signOut } from '@/redux/auth/signin/slice';
import { IoLogOutOutline } from 'react-icons/io5';

type UserLike = {
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar?: string | null;
} | null;

type Props = {
  // Optional: allow parent to pass a user object; otherwise we read from Redux
  user?: UserLike;
};

const UserMenu: React.FC<Props> = ({ user: userProp }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const reduxUser = useAppSelector((s: any) => s.auth?.signin?.user);
  const user = userProp ?? reduxUser;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const displayName =
      (user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.first_name) || user?.username;

    if (displayName) {
      localStorage.setItem('username', displayName.trim());
    }

    if (user) {
      try {
        localStorage.setItem('userProfile', JSON.stringify(user));
      } catch {
        // ignore
      }
    }
  }, [user]);

  const storedProfile =
    typeof window !== 'undefined'
      ? (() => {
          try {
            const raw = localStorage.getItem('userProfile');
            return raw ? JSON.parse(raw) : null;
          } catch {
            return null;
          }
        })()
      : null;

  const fullName =
    (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.first_name) ||
    user?.username ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('username') || 'User'
      : 'User');

  const email = user?.email || storedProfile?.email || '---'; // fallback when email is not available

  const initials = useMemo(() => {
    const n = (fullName || 'U').trim();
    const parts = n.split(' ');
    const i = (parts[0]?.[0] || 'U') + (parts[1]?.[0] || '');
    return <i className="capitalize">{i}</i>;
  }, [fullName]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        localStorage.removeItem('userProfile');
        localStorage.setItem(
          'auth:event',
          JSON.stringify({ type: 'logout', ts: Date.now() })
        );
        localStorage.removeItem('auth:event');
      } catch {
        // ignore
      }
    }

    dispatch(signOut());
    router.push('/account/signin');
  };

  const items = [
    { key: 'profile', label: <Link href="/account/profile">My Profile</Link> },
    { key: 'account', label: <Link href="/account">My Account</Link> },
    { type: 'divider' as const },
    { key: 'logout', label: <span onClick={handleLogout}>Logout</span> },
  ];

  return (
    <Dropdown
      overlayStyle={{ zIndex: 40 }}
      menu={{ items }}
      placement="bottomRight"
      trigger={['click']}
    >
      <div className="inline-flex items-center gap-3 bg-amber-50 px-1 cursor-pointer py-1 w-full rounded-md hover:bg-amber-100 transition !z-40">
        <Avatar
          size="large"
          icon={!initials ? <UserOutlined /> : undefined}
          className="flex-shrink-0"
        >
          {initials}
        </Avatar>

        <div className="flex-1 min-w-0 flex flex-col">
          <span className="text-sm font-semibold text-gray-900 leading-tight truncate">
            {fullName}
          </span>
          <span className="text-xs text-gray-500 leading-tight truncate">
            {email || '---'}
          </span>
        </div>

        <IoLogOutOutline className="ml-1 text-gray-400 text-lg flex-shrink-0" />
      </div>
    </Dropdown>
  );
};

export default UserMenu;
